import React from 'react';
import type { CategoryDefinition, CategoryHubStats } from '../../../types/category-hub';
import { getCategoryBySlug } from '../../../utils/category/category-hub-config';
import { generateCategoryMarketHeaderText } from '../../../utils/category/generate-category-market-header-text';
import { buildFaqPageSchema } from '../../../utils/seo/build-faq-page-schema';
import { buildLocationSrpPath } from '../../../utils/location/build-location-srp-path';

export interface CategoryHeaderProps {
  category: CategoryDefinition;
  locale: 'en' | 'fi' | 'sv';
  categoryHubData: CategoryHubStats | null;
  className?: string;
}

const JOBS_SEGMENT_BY_LOCALE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'jobs',
  fi: 'tyopaikat',
  sv: 'lediga-jobb',
};

const CATEGORY_SEGMENT_BY_LOCALE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'category',
  fi: 'kategoria',
  sv: 'kategori',
};

const FAQ_SECTION_TITLE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'Frequently asked questions',
  fi: 'Usein kysytyt kysymykset',
  sv: 'Vanliga frågor',
};

const POPULAR_LOCATIONS_TITLE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'Popular locations',
  fi: 'Suositut sijainnit',
  sv: 'Populära orter',
};

const RELATED_CATEGORIES_TITLE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'Related categories',
  fi: 'Aiheeseen liittyvät kategoriat',
  sv: 'Relaterade kategorier',
};

function buildH1(displayName: string, locale: 'en' | 'fi' | 'sv'): string {
  if (locale === 'fi') return `${displayName} – työpaikat Suomessa`;
  if (locale === 'sv') return `${displayName} – jobb i Finland`;
  return `${displayName} Jobs in Finland`;
}

function buildCategoryUrl(locale: 'en' | 'fi' | 'sv', slug: string): string {
  return `/${locale}/${JOBS_SEGMENT_BY_LOCALE[locale]}/${CATEGORY_SEGMENT_BY_LOCALE[locale]}/${slug}`;
}

// Combined with the current category so "Popular locations" links to the genuine sub-query
// (e.g. "Software Developer jobs in Helsinki"), not a plain location page that drops the
// category filter — see TEC-322.
function buildLocationUrl(locale: 'en' | 'fi' | 'sv', location: string, categorySlug: string): string {
  return `/${locale}${buildLocationSrpPath(location, locale, categorySlug)}`;
}

/**
 * Static SEO header for `/jobs/category/{slug}` pages: static H1 + data-driven intro,
 * popular-locations and related-categories internal links, and a visible FAQ accordion
 * (with matching FAQPage JSON-LD) — everything present in the initial server-rendered HTML.
 * Rendered by SearchHeader.tsx as a second, lower-priority branch alongside MarketHeader.
 */
export const CategoryHeader: React.FC<CategoryHeaderProps> = ({
  category,
  locale,
  categoryHubData,
  className = '',
}) => {
  const displayName = category.displayNames[locale];
  const jobCount = categoryHubData?.jobCount ?? 0;
  const topLocations = categoryHubData?.topLocations ?? [];
  const remotePercentage = categoryHubData?.remotePercentage ?? 0;

  const relatedCategories = category.relatedCategorySlugs
    .map((slug) => getCategoryBySlug(slug))
    .filter((c): c is CategoryDefinition => c != null)
    .map((c) => ({ slug: c.slug, displayName: c.displayNames[locale] }));

  const { introParagraph, faqItems } = generateCategoryMarketHeaderText({
    categoryDisplayName: displayName,
    locale,
    jobCount,
    topLocations,
    remotePercentage,
    relatedCategories,
    avgSalaryFormatted: null,
  });

  const faqSchema = faqItems.length > 0 ? buildFaqPageSchema(faqItems) : null;

  return (
    <>
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      <section
        id="category-info-header"
        className={`category-info-header relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[#F8F5F0] via-[#F2EADF] to-[#C8DDCE]/50 dark:from-[#170F07] dark:via-[#2A1D0F] dark:to-[#1A2E22] p-6 sm:p-8 lg:p-12 ${className}`}
        aria-labelledby="category-header-title"
      >
        <h1
          id="category-header-title"
          className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF] mb-3"
        >
          {buildH1(displayName, locale)}
        </h1>

        <p className="text-base sm:text-lg leading-relaxed max-w-5xl text-[#3D3529] dark:text-[#C4A882] mb-6">
          {introParagraph}
        </p>

        {(topLocations.length > 0 || relatedCategories.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            {topLocations.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] mb-2">
                  {POPULAR_LOCATIONS_TITLE[locale]}
                </h2>
                <ul className="flex flex-wrap gap-2">
                  {topLocations.map((loc) => (
                    <li key={loc.location}>
                      <a
                        href={buildLocationUrl(locale, loc.location, category.slug)}
                        className="inline-block rounded-full border border-[#DDD5C8] dark:border-[#4A3520] bg-white/70 dark:bg-[#1F1509]/80 px-3 py-1.5 text-sm text-[#1E1A14] dark:text-[#F2EADF] hover:bg-white dark:hover:bg-[#2A1D0F] transition-colors"
                      >
                        {loc.location.split(',')[0].trim()}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {relatedCategories.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] mb-2">
                  {RELATED_CATEGORIES_TITLE[locale]}
                </h2>
                <ul className="flex flex-wrap gap-2">
                  {relatedCategories.map((related) => (
                    <li key={related.slug}>
                      <a
                        href={buildCategoryUrl(locale, related.slug)}
                        className="inline-block rounded-full border border-[#DDD5C8] dark:border-[#4A3520] bg-white/70 dark:bg-[#1F1509]/80 px-3 py-1.5 text-sm text-[#1E1A14] dark:text-[#F2EADF] hover:bg-white dark:hover:bg-[#2A1D0F] transition-colors"
                      >
                        {related.displayName}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {faqItems.length > 0 && (
          <section aria-labelledby="category-faq-title">
            <h2
              id="category-faq-title"
              className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF] mb-3"
            >
              {FAQ_SECTION_TITLE[locale]}
            </h2>
            <div className="flex flex-col gap-1">
              {faqItems.map((faq, idx) => (
                <details
                  key={idx}
                  className="group rounded-xl border border-[#DDD5C8]/70 dark:border-[#4A3520]/85 bg-white/85 dark:bg-[#1F1509]/95"
                >
                  <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] marker:content-none sm:px-5 sm:text-base [&::-webkit-details-marker]:hidden">
                    <span className="flex items-center justify-between gap-3">
                      <span>{faq.question}</span>
                      <span className="text-[#7A6E5F] dark:text-[#B8A48C] -rotate-90 transition group-open:rotate-0" aria-hidden>
                        ▼
                      </span>
                    </span>
                  </summary>
                  <div className="border-t border-[#DDD5C8]/70 dark:border-[#4A3520]/85 px-4 py-3 text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882] sm:px-5 sm:text-base">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}
      </section>
    </>
  );
};
